// libraries
import React from 'react';
import { Form, Dropdown } from 'react-bootstrap';
import { NumericFormat } from 'react-number-format';

// config
import { FieldConfig } from '@domains/documents-review/config/fieldConfigs';

interface EditableFieldByTypeProps {
  field: string;
  config: FieldConfig;
  value: string;
  onChange: (value: string) => void;
}

export const EditableFieldByType: React.FC<EditableFieldByTypeProps> = ({
  field,
  config,
  value,
  onChange,
}) => {
  switch (config.type) {
    case 'select':
      return (
        <Form.Select
          value={value}
          onChange={e => onChange(e.target.value)}
          className="form-select"
        >
          <option value="">Seleccione una opción</option>
          {config.options?.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      );

    case 'multiselect': {
      // Parse current selected values (separated by ";")
      const selectedValues = value
        ? value.split(/[;,]/).filter(v => v.trim() !== '')
        : [];

      const handleToggle = (optionValue: string | number) => {
        const stringValue = String(optionValue);
        const isSelected = selectedValues.includes(stringValue);
        const isNone = stringValue === 'none';

        let newValues: string[];
        if (isSelected) {
          // If deselecting
          newValues = selectedValues.filter(v => v !== stringValue);
        } else {
          // If selecting
          if (isNone) {
            // If selecting "Ninguno", clear all other options
            newValues = ['none'];
          } else if (isNoneSelected) {
            // If "Ninguno" is selected and user selects another option, remove "Ninguno"
            newValues = [stringValue];
          } else {
            // Normal selection
            newValues = [...selectedValues, stringValue];
          }
        }

        onChange(newValues.join(';'));
      };

      const isNoneSelected = selectedValues.includes('none');

      // Get selected option labels
      const selectedLabels = selectedValues
        .map(val => {
          const option = config.options?.find(opt => String(opt.value) === val);
          return option?.label;
        })
        .filter(Boolean);

      return (
        <div>
          <Dropdown>
            <Dropdown.Toggle
              className="w-100 multi-dropdown"
              variant="light"
              style={{
                textAlign: 'left',
                backgroundColor: '#fff',
                borderColor: '#dee2e6',
              }}
            >
              {selectedValues.length > 0
                ? `${selectedValues.length} opción(es) seleccionada(s)`
                : 'Seleccionar opciones'}
            </Dropdown.Toggle>
            <Dropdown.Menu
              className="wrapper-items"
              flip={true}
              style={{
                maxHeight: '300px',
                overflowY: 'auto',
                width: '100%',
              }}
            >
              {config.options?.map(option => {
                const optionValue = String(option.value);
                const isOptionNone = optionValue === 'none';
                const isDisabled = !isOptionNone && isNoneSelected;
                const isChecked = selectedValues.includes(optionValue);

                return (
                  <Dropdown.Item
                    key={option.value}
                    as="div"
                    onClick={e => e.stopPropagation()}
                  >
                    <Form.Check
                      type="checkbox"
                      id={`${field}-${option.value}`}
                      label={option.label}
                      checked={isChecked}
                      disabled={isDisabled}
                      onChange={() => handleToggle(option.value)}
                    />
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
          {selectedLabels.length > 0 && (
            <div
              className="mt-2 p-2 border rounded"
              style={{
                backgroundColor: '#f8f9fa',
                fontSize: '0.9rem',
              }}
            >
              <strong>Opciones seleccionadas:</strong>
              <div className="mt-1">{selectedLabels.join(' • ')}</div>
            </div>
          )}
        </div>
      );
    }

    case 'number':
      return (
        <Form.Control
          type="number"
          min={config.min ?? 0}
          max={config.max ?? 999}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={config.placeholder}
        />
      );

    case 'date':
      return (
        <Form.Control
          type="date"
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      );

    case 'price':
      return (
        <NumericFormat
          value={value}
          thousandSeparator="."
          decimalSeparator=","
          prefix="$ "
          allowNegative={false}
          placeholder={config.placeholder || 'Ingrese el precio'}
          onValueChange={values => onChange(values.value)}
          className="form-control"
        />
      );

    case 'text':
    default:
      return (
        <Form.Control
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          maxLength={config.maxLength}
          placeholder={config.placeholder || 'Ingrese el valor'}
        />
      );
  }
};
